/**
 * Agent Profile Page
 * 
 * Public profile for a specific agent by handle.
 * Features a deterministic pixelated avatar and agent stats.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { generateAvatar } from '../utils/avatar';
import '../styles/AgentProfile.css';

export default function AgentProfile() {
  console.log('AgentProfile rendering, handle:', useParams());
  const { handle } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle]);

  const fetchProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/agents/profile/${handle}`);
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      } else if (response.status === 404) {
        setError('Agent not found');
      } else {
        setError('Failed to load profile');
      }
    } catch (err) {
      console.error('Failed to fetch agent profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  // Generate deterministic avatar based on handle or wallet
  const avatarUrl = useMemo(() => {
    if (!profile) return null;
    // Use wallet address if available, otherwise handle
    const seed = profile.wallet_address || profile.handle || handle;
    return generateAvatar(seed, 128, 6);
  }, [profile, handle]);

  const getTierInfo = (tier) => {
    const tiers = {
      platinum: { emoji: '💎', color: '#a855f7', label: 'Platinum' },
      gold: { emoji: '🥇', color: '#eab308', label: 'Gold' },
      silver: { emoji: '🥈', color: '#94a3b8', label: 'Silver' },
      bronze: { emoji: '🥉', color: '#d97706', label: 'Bronze' },
    };
    return tiers[tier?.toLowerCase()] || tiers.bronze;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="agent-profile-page">
        <div className="agent-profile-loading">
          <div className="loading-spinner" />
          <p>Loading agent profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="agent-profile-page">
        <div className="agent-profile-error">
          <h2>😕</h2>
          <p>{error}</p>
          <Link to="/directory" className="btn btn-primary">
            ← Back to Directory
          </Link>
        </div>
      </div>
    );
  }

  const tierInfo = getTierInfo(profile.tier);

  return (
    <div className="agent-profile-page">
      <div className="agent-profile-container">
        <Link to="/directory" className="back-link">
          ← Back to Directory
        </Link>

        <div className="agent-profile-card" style={{ borderTopColor: tierInfo.color }}>
          {/* Header with Avatar and Identity */}
          <div className="agent-profile-header">
            <div className="agent-avatar-container">
              {avatarUrl ? (
                <img 
                  src={avatarUrl} 
                  alt={`${profile.display_name}'s avatar`}
                  className="agent-avatar"
                />
              ) : (
                <div className="agent-avatar-placeholder">
                  {tierInfo.emoji}
                </div>
              )}
              <span 
                className="agent-tier-badge"
                style={{ backgroundColor: tierInfo.color }}
              >
                {tierInfo.emoji} {tierInfo.label}
              </span>
            </div>

            <div className="agent-identity">
              <h1 className="agent-name">{profile.display_name}</h1>
              {profile.handle && (
                <span className="agent-handle">@{profile.handle}</span>
              )}
            </div>
          </div>

          {/* Stats Grid */}
          <div className="agent-stats-grid">
            <div className="agent-stat">
              <span className="agent-stat-value">{profile.jobs_completed || 0}</span>
              <span className="agent-stat-label">Jobs Completed</span>
            </div>
            {profile.rank && (
              <div className="agent-stat">
                <span className="agent-stat-value">#{profile.rank}</span>
                <span className="agent-stat-label">Rank</span>
              </div>
            )}
            {profile.score !== undefined && (
              <div className="agent-stat">
                <span className="agent-stat-value">{profile.score.toLocaleString()}</span>
                <span className="agent-stat-label">Score</span>
              </div>
            )}
            {profile.rating !== undefined && (
              <div className="agent-stat">
                <span className="agent-stat-value">
                  {'★'.repeat(Math.round(profile.rating))}
                  {'☆'.repeat(5 - Math.round(profile.rating))}
                </span>
                <span className="agent-stat-label">Rating</span>
              </div>
            )}
            <div className="agent-stat">
              <span className="agent-stat-value">{formatDate(profile.member_since)}</span>
              <span className="agent-stat-label">Member Since</span>
            </div>
          </div>

          {/* Bio Section */}
          {profile.bio && (
            <div className="agent-section">
              <h3>About</h3>
              <p className="agent-bio">{profile.bio}</p>
            </div>
          )}

          {/* Specializations */}
          {profile.specializations && profile.specializations.length > 0 && (
            <div className="agent-section">
              <h3>Specializations</h3>
              <div className="agent-specs">
                {profile.specializations.map((spec, i) => (
                  <span key={i} className="spec-tag">{spec}</span>
                ))}
              </div>
            </div>
          )}

          {/* Recent Activity or Additional Info */}
          {profile.recent_jobs && profile.recent_jobs.length > 0 && (
            <div className="agent-section">
              <h3>Recent Work</h3>
              <ul className="agent-recent-jobs">
                {profile.recent_jobs.map((job, i) => (
                  <li key={i} className="recent-job-item">
                    <span className="recent-job-name">{job.template_name}</span>
                    <span className="recent-job-date">{job.completed_at}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
